import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import { SG_FETCH_DEBRIEF_SCHEDULE } from "constants/actions";

const STORAGE_KEY = "comparisonParticipationSelection";
const DEFAULT_CATEGORY_LABEL = "Uncategorized";
const AUTO_PARENT_KEY = "__auto__";

const formatNumber = (value) =>
  new Intl.NumberFormat().format(value ?? 0);

const formatPercent = (ratio) => {
  if (!Number.isFinite(ratio)) return "—";
  if (ratio === 0) return "0%";
  return new Intl.NumberFormat(undefined, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(ratio);
};

const buildParentKey = (parent) => {
  if (!parent) return AUTO_PARENT_KEY;
  if (parent.id != null) return `id:${parent.id}`;
  if (parent.priority) return `priority:${parent.priority}`;
  if (parent.name) return `name:${parent.name}`;
  return JSON.stringify(parent);
};

const coalesceFiniteNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return null;
};

const buildParentOptionLookup = (parent) => {
  const map = new Map();
  if (!parent) return map;

  const register = (keys, meta) => {
    keys.forEach((value) => {
      if (value == null) return;
      const key = String(value);
      if (!map.has(key)) {
        map.set(key, meta);
      }
    });
  };

  const visit = (option) => {
    if (!option) return;
    const label =
      option?.name ??
      option?.label ??
      option?.text ??
      option?.value_name ??
      option?.value ??
      DEFAULT_CATEGORY_LABEL;
    const identity =
      (option?.value_id != null && `value:${option.value_id}`) ||
      (option?.id != null && `id:${option.id}`) ||
      (option?.option_id != null && `option:${option.option_id}`) ||
      (option?.key != null && `key:${option.key}`) ||
      (option?.value != null && `value:${option.value}`) ||
      `label:${label}`;
    const meta = {
      label,
      identity,
    };
    const keys = [];
    const rawId =
      option?.value_id ??
      option?.id ??
      option?.value ??
      option?.option_id ??
      option?.key ??
      null;
    if (rawId != null) keys.push(rawId);
    if (option?.name) keys.push(option.name);
    if (option?.label) keys.push(option.label);
    if (option?.text) keys.push(option.text);
    if (option?.value_name) keys.push(option.value_name);
    if (option?.value) keys.push(option.value);
    if (label) {
      keys.push(label);
      if (typeof label === "string") {
        keys.push(label.toLowerCase());
      }
    }
    register(keys, meta);
    if (option?.options) option.options.forEach(visit);
    if (option?.subfactors) option.subfactors.forEach(visit);
  };

  (parent?.options || []).forEach(visit);
  return map;
};

const extractId = (value) => {
  const visited = new WeakSet();

  const coerce = (input) => {
    if (input == null) return null;
    if (typeof input === "string") {
      const trimmed = input.trim();
      return trimmed ? trimmed : null;
    }
    if (typeof input === "number") {
      return Number.isFinite(input) ? String(input) : null;
    }
    if (typeof input === "bigint") {
      return input.toString();
    }
    return null;
  };

  const inner = (input) => {
    if (input == null) return null;

    const primitive = coerce(input);
    if (primitive) return primitive;

    if (typeof input !== "object") {
      return null;
    }

    if (visited.has(input)) {
      return null;
    }
    visited.add(input);

    if (Array.isArray(input)) {
      for (const entry of input) {
        const candidate = inner(entry);
        if (candidate) return candidate;
      }
      return null;
    }

    const candidateKeys = [
      "employee_id",
      "employeeId",
      "employee",
      "member_id",
      "memberId",
      "member",
      "participant_id",
      "participantId",
      "participant",
      "recipient_id",
      "recipientId",
      "recipient",
      "respondent_id",
      "respondentId",
      "respondent",
      "user_id",
      "userId",
      "user",
      "person_id",
      "personId",
      "person",
      "contact_id",
      "contactId",
      "contact",
      "target_id",
      "targetId",
      "target",
      "task_response",
      "assignee",
      "individual",
      "profile",
      "id",
    ];

    for (const key of candidateKeys) {
      if (key in input) {
        const candidate = inner(input[key]);
        if (candidate) return candidate;
      }
    }

    return null;
  };

  return inner(value);
};

const resolveEmployeeId = (...values) => {
  for (const value of values) {
    const extracted = extractId(value);
    if (extracted) {
      return extracted;
    }
  }
  return null;
};

const resolveScheduleId = (selectedDebrief, debriefData) => {
  const candidates = [];
  const register = (value) => {
    const extracted = extractId(value);
    if (!extracted) return;
    const numeric = Number(extracted);
    if (!Number.isFinite(numeric) || numeric <= 0) return;
    if (!candidates.includes(numeric)) {
      candidates.push(numeric);
    }
  };

  const registerFrom = (source) => {
    if (!source) return;
    register(source?.debrief_schedule);
    register(source?.debrief_schedule_id);
    register(source?.schedule);
    register(source?.schedule_id);
    register(source?.survey_schedule);
    register(source?.survey_schedule_id);
    register(source?.survey_event);
    register(source?.survey_event_id);
    register(source?.parent_schedule);
    register(source?.parent_schedule_id);
    register(source?.report);
    register(source?.report_id);
    register(source?.brief);
    register(source?.brief_id);
    register(source?.id);
  };

  registerFrom(selectedDebrief);

  if (Array.isArray(selectedDebrief?.responses)) {
    selectedDebrief.responses.forEach((response) => {
      registerFrom(response);
      registerFrom(response?.response);
    });
  }

  registerFrom(debriefData);
  registerFrom(debriefData?.schedule);

  if (Array.isArray(debriefData?.reports)) {
    debriefData.reports.forEach((report) => {
      registerFrom(report);
      if (Array.isArray(report?.responses)) {
        report.responses.forEach((response) => {
          registerFrom(response);
          registerFrom(response?.response);
        });
      }
    });
  }

  if (Array.isArray(debriefData?.allResponses)) {
    debriefData.allResponses.forEach((response) => {
      registerFrom(response);
      registerFrom(response?.response);
    });
  }

  register(debriefData?.debrief_schedule);
  register(debriefData?.debrief_schedule_id);
  register(debriefData?.schedule_id);
  register(debriefData?.survey_schedule_id);

  return candidates.length > 0 ? candidates[0] : null;
};

const buildCategoryIndex = (employeeCategories) => {
  const map = new Map();
  (employeeCategories || []).forEach((entry) => {
    const employeeId =
      entry?.employee_id ?? entry?.employee ?? entry?.id ?? entry?.user;
    if (employeeId == null) return;

    const categories = Array.isArray(entry?.category)
      ? entry.category
      : [];
    const normalized = categories
      .map((category) => ({
        priority: category?.priority || null,
        valueId:
          category?.value_id ??
          category?.id ??
          category?.value ??
          category?.category_id ??
          null,
        name:
          category?.value_name ??
          category?.name ??
          category?.label ??
          category?.value ??
          null,
        label:
          category?.value_name ||
          category?.value ||
          category?.label ||
          category?.name ||
          category?.text ||
          category?.display ||
          DEFAULT_CATEGORY_LABEL,
      }))
      .filter(Boolean);
    map.set(String(employeeId), normalized);
  });
  return map;
};

const matchesParent = (category, parent) => {
  if (!parent) return false;
  if (parent.priority && category.priority === parent.priority) return true;
  if (parent.id != null && category.valueId != null) {
    if (String(category.valueId) === String(parent.id)) return true;
  }
  if (parent.name && category.name && category.name === parent.name) return true;
  const options = Array.isArray(parent.options) ? parent.options : [];
  if (
    options.some(
      (option) =>
        String(option?.id) === String(category.valueId) ||
        option?.name === category.name
    )
  ) {
    return true;
  }
  return false;
};

const createCategoryResult = (label, identity, parentKey) => {
  const safeLabel =
    typeof label === "string" && label.trim()
      ? label.trim()
      : DEFAULT_CATEGORY_LABEL;
  const safeParentKey = parentKey || AUTO_PARENT_KEY;
  const normalizedIdentity =
    identity ||
    (typeof safeLabel === "string"
      ? `label:${safeLabel.toLowerCase()}`
      : `label:${safeLabel}`);
  return {
    label: safeLabel,
    key: `${safeParentKey}::${normalizedIdentity}`,
  };
};

const assignCategory = (
  employeeId,
  categoryIndex,
  selectedParentOption
) => {
  const parentKey = selectedParentOption?.key || AUTO_PARENT_KEY;
  const fallback = createCategoryResult(
    DEFAULT_CATEGORY_LABEL,
    null,
    parentKey
  );

  if (!employeeId) return fallback;
  const categories = categoryIndex.get(employeeId);
  if (!categories || categories.length === 0) return fallback;

  const parent = selectedParentOption?.config;
  const optionLookup = selectedParentOption?.optionLookup;

  const resolveWithMeta = (category, meta) => {
    const labelCandidate =
      meta?.label ??
      category?.label ??
      category?.name ??
      DEFAULT_CATEGORY_LABEL;
    const identityCandidate =
      meta?.identity ??
      (category?.valueId != null
        ? `value:${category.valueId}`
        : category?.name
        ? `name:${category.name}`
        : category?.priority
        ? `priority:${category.priority}`
        : null);
    return createCategoryResult(labelCandidate, identityCandidate, parentKey);
  };

  if (optionLookup && optionLookup.size > 0) {
    for (const category of categories) {
      const valueId =
        category?.valueId != null ? String(category.valueId) : null;
      if (valueId && optionLookup.has(valueId)) {
        return resolveWithMeta(category, optionLookup.get(valueId));
      }
      if (category?.label) {
        if (optionLookup.has(category.label)) {
          return resolveWithMeta(category, optionLookup.get(category.label));
        }
        const lower = category.label.toLowerCase();
        if (optionLookup.has(lower)) {
          return resolveWithMeta(category, optionLookup.get(lower));
        }
      }
      if (category?.name && optionLookup.has(category.name)) {
        return resolveWithMeta(category, optionLookup.get(category.name));
      }
    }
  }

  if (parent) {
    for (const category of categories) {
      if (matchesParent(category, parent)) {
        return resolveWithMeta(category);
      }
    }
    return fallback;
  }

  const secondary = categories.find(
    (category) => category.priority === "secondary"
  );
  if (secondary) return resolveWithMeta(secondary);

  const primary = categories.find(
    (category) => category.priority === "primary"
  );
  if (primary) return resolveWithMeta(primary);

  return resolveWithMeta(categories[0]);
};

const buildCategoryRows = (
  recipientSet,
  completedSet,
  categoryIndex,
  selectedParentOption
) => {
  const buckets = new Map();

  recipientSet.forEach((employeeId) => {
    const category = assignCategory(
      employeeId,
      categoryIndex,
      selectedParentOption
    );
    if (!category) return;
    if (!buckets.has(category.key)) {
      buckets.set(category.key, {
        key: category.key,
        label: category.label,
        recipients: new Set(),
        completed: new Set(),
      });
    }
    const bucket = buckets.get(category.key);
    bucket.label = category.label;
    bucket.recipients.add(employeeId);
    if (completedSet.has(employeeId)) {
      bucket.completed.add(employeeId);
    }
  });

  const rows = Array.from(buckets.values()).map((bucket) => {
    const total = bucket.recipients.size;
    const completed = bucket.completed.size;
    const pending = Math.max(total - completed, 0);
    return {
      key: bucket.key,
      label: bucket.label,
      total,
      completed,
      pending,
      rate: total > 0 ? completed / total : 0,
    };
  });

  rows.sort((a, b) => b.total - a.total);
  return rows;
};

const convergeSets = (...sets) => {
  const merged = new Set();
  sets.forEach((set) => {
    set?.forEach((value) => merged.add(value));
  });
  return merged;
};

const ComparisonParticipationTab = ({
  pulseSurveyData,
  debriefData,
  selectedDebrief,
  coreData,
  employeeCategories,
}) => {
  const dispatch = useDispatch();

  const { debriefSchedules } = useSelector(
    (state) => ({
      debriefSchedules: state.debrief_schedule,
    }),
    shallowEqual
  );

  const scheduleId = useMemo(
    () => resolveScheduleId(selectedDebrief, debriefData),
    [selectedDebrief, debriefData]
  );

  const scheduleFetchRef = useRef(null);
  useEffect(() => {
    if (!scheduleId) {
      scheduleFetchRef.current = null;
      return;
    }
    if (scheduleFetchRef.current === scheduleId) return;
    scheduleFetchRef.current = scheduleId;
    dispatch({
      type: SG_FETCH_DEBRIEF_SCHEDULE,
      payload: { id: scheduleId },
    });
  }, [dispatch, scheduleId]);

  const schedule = useMemo(() => {
    if (scheduleId && debriefSchedules) {
      const key = String(scheduleId);
      const entry =
        debriefSchedules?.[key] ??
        debriefSchedules?.[Number(key)] ??
        null;
      if (entry) {
        return entry;
      }
    }
    return (
      debriefData?.schedule ||
      selectedDebrief?.schedule ||
      selectedDebrief ||
      null
    );
  }, [
    debriefSchedules,
    scheduleId,
    selectedDebrief,
    debriefData,
  ]);

  useEffect(() => {
    try {
      const snapshot = {
        selectedDebrief,
        debriefData,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (error) {
      // best effort
    }
  }, [selectedDebrief, debriefData]);

  const categoryIndex = useMemo(
    () => buildCategoryIndex(employeeCategories),
    [employeeCategories]
  );

  const categoryParents = useMemo(() => {
    const parents = coreData?.categories?.[0]?.categories || [];
    return parents.filter((parent) => parent?.visibility !== "private");
  }, [coreData]);

  const parentOptions = useMemo(() => {
    const options = [
      {
        key: AUTO_PARENT_KEY,
        label: "Auto grouping (priority based)",
        config: null,
        optionLookup: null,
      },
    ];
    categoryParents.forEach((parent) => {
      options.push({
        key: buildParentKey(parent),
        label:
          parent?.name ||
          parent?.label ||
          (parent?.priority ? `Priority: ${parent.priority}` : "Category"),
        config: parent,
        optionLookup: buildParentOptionLookup(parent),
      });
    });
    return options;
  }, [categoryParents]);

  const [selectedParentKey, setSelectedParentKey] = useState(
    parentOptions[0]?.key || AUTO_PARENT_KEY
  );

  useEffect(() => {
    if (!parentOptions.find((option) => option.key === selectedParentKey)) {
      setSelectedParentKey(parentOptions[0]?.key || AUTO_PARENT_KEY);
    }
  }, [parentOptions, selectedParentKey]);

  const selectedParentOption = useMemo(() => {
    const match =
      parentOptions.find((option) => option.key === selectedParentKey) ||
      parentOptions[0];
    return match || null;
  }, [parentOptions, selectedParentKey]);

  const pulseRecipients = useMemo(() => {
    const set = new Set();
    (pulseSurveyData?.participants || []).forEach((participant) => {
      const id = resolveEmployeeId(
        participant,
        participant?.member_id,
        participant?.member,
        participant?.employee_id,
        participant?.employee,
        participant?.user_id,
        participant?.user,
        participant?.contact_id,
        participant?.contact,
        participant?.person_id,
        participant?.person,
        participant?.id
      );
      if (id) {
        set.add(id);
      }
    });
    return set;
  }, [pulseSurveyData]);

  const pulseCompleted = useMemo(() => {
    const set = new Set();
    (pulseSurveyData?.responses || []).forEach((response) => {
      const id = resolveEmployeeId(
        response,
        response?.employee,
        response?.member,
        response?.member_id,
        response?.task_response,
        response?.task_response?.employee_id,
        response?.participant,
        response?.participant_id
      );
      if (id) {
        set.add(id);
      }
    });
    return set;
  }, [pulseSurveyData]);

  const surveyRequested = useMemo(() => {
    const set = new Set();
    const add = (value) => {
      const id = resolveEmployeeId(
        value,
        value?.employee,
        value?.employee_id,
        value?.member,
        value?.member_id,
        value?.user,
        value?.user_id,
        value?.contact,
        value?.contact_id,
        value?.person,
        value?.person_id
      );
      if (id) set.add(id);
    };

    schedule?.request_list?.forEach(add);
    (selectedDebrief?.request_list || []).forEach(add);
    (debriefData?.request_list || []).forEach(add);
    (debriefData?.schedule?.request_list || []).forEach(add);
    (debriefData?.reports || []).forEach((report) =>
      report?.request_list?.forEach(add)
    );

    return set;
  }, [schedule, selectedDebrief, debriefData]);

  const surveyCompleted = useMemo(() => {
    const set = new Set();
    const add = (value) => {
      const id = resolveEmployeeId(
        value,
        value?.employee,
        value?.employee_id,
        value?.member,
        value?.member_id,
        value?.user,
        value?.user_id,
        value?.contact,
        value?.contact_id,
        value?.person,
        value?.person_id
      );
      if (id) set.add(id);
    };

    schedule?.compliance?.forEach(add);
    (debriefData?.schedule?.compliance || []).forEach(add);
    (debriefData?.allResponses || []).forEach((response) => add(response));
    (selectedDebrief?.responses || []).forEach((response) => add(response));
    (selectedDebrief?.compliance || []).forEach(add);
    (debriefData?.reports || []).forEach((report) => {
      report?.compliance?.forEach(add);
      report?.responses?.forEach((response) => add(response));
    });

    return set;
  }, [schedule, debriefData, selectedDebrief]);

  const mergedPulseRecipients = useMemo(
    () => convergeSets(pulseRecipients, pulseCompleted),
    [pulseRecipients, pulseCompleted]
  );

  const surveyUniverse = useMemo(
    () => convergeSets(surveyRequested, surveyCompleted),
    [surveyRequested, surveyCompleted]
  );

  const pulseRows = useMemo(
    () =>
      buildCategoryRows(
        mergedPulseRecipients,
        pulseCompleted,
        categoryIndex,
        selectedParentOption
      ),
    [mergedPulseRecipients, pulseCompleted, categoryIndex, selectedParentOption]
  );

  const surveyRows = useMemo(() => {
    const recipientSet =
      surveyRequested.size > 0 ? surveyRequested : surveyUniverse;
    return buildCategoryRows(
      recipientSet,
      surveyCompleted,
      categoryIndex,
      selectedParentOption
    );
  }, [
    surveyRequested,
    surveyUniverse,
    surveyCompleted,
    categoryIndex,
    selectedParentOption,
  ]);

  const overlapRows = useMemo(() => {
    const buckets = new Map();

    const ensureBucket = (category) => {
      if (!category) return null;
      if (!buckets.has(category.key)) {
        buckets.set(category.key, {
          key: category.key,
          label: category.label,
          overlapCount: 0,
          pulseCompleted: 0,
          surveyCompleted: 0,
        });
      }
      const bucket = buckets.get(category.key);
      if (!bucket.label && category.label) {
        bucket.label = category.label;
      }
      return bucket;
    };

    pulseRows.forEach((row) => {
      const bucket = ensureBucket({ key: row.key, label: row.label });
      if (bucket) {
        bucket.pulseCompleted = row.completed;
      }
    });

    surveyRows.forEach((row) => {
      const bucket = ensureBucket({ key: row.key, label: row.label });
      if (bucket) {
        bucket.surveyCompleted = row.completed;
      }
    });

    const pulseResponseBuckets = new Map();
    (pulseSurveyData?.responses || []).forEach((response) => {
      const employeeId = resolveEmployeeId(
        response,
        response?.employee,
        response?.member,
        response?.member_id,
        response?.task_response,
        response?.task_response?.employee_id,
        response?.participant,
        response?.participant_id
      );
      if (!employeeId) return;
      const category = assignCategory(
        employeeId,
        categoryIndex,
        selectedParentOption
      );
      const bucket = ensureBucket(category);
      if (!bucket) return;
      if (!pulseResponseBuckets.has(category.key)) {
        pulseResponseBuckets.set(category.key, new Set());
      }
      pulseResponseBuckets.get(category.key).add(employeeId);
    });

    const surveyResponseBuckets = new Map();
    const collectSurveyResponse = (response) => {
      const employeeId = resolveEmployeeId(
        response,
        response?.employee,
        response?.member,
        response?.member_id,
        response?.participant,
        response?.participant_id,
        response?.respondent,
        response?.respondent_id
      );
      if (!employeeId) return;
      const category = assignCategory(
        employeeId,
        categoryIndex,
        selectedParentOption
      );
      const bucket = ensureBucket(category);
      if (!bucket) return;
      if (!surveyResponseBuckets.has(category.key)) {
        surveyResponseBuckets.set(category.key, new Set());
      }
      surveyResponseBuckets.get(category.key).add(employeeId);
    };

    (debriefData?.allResponses || []).forEach(collectSurveyResponse);
    (selectedDebrief?.responses || []).forEach(collectSurveyResponse);
    (debriefData?.reports || []).forEach((report) =>
      report?.responses?.forEach(collectSurveyResponse)
    );

    pulseResponseBuckets.forEach((pulseSet, key) => {
      const surveySet = surveyResponseBuckets.get(key);
      if (!surveySet) return;
      let count = 0;
      pulseSet.forEach((employeeId) => {
        if (surveySet.has(employeeId)) {
          count += 1;
        }
      });
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.overlapCount = count;
      }
    });

    return Array.from(buckets.values())
      .map((bucket) => ({
        key: bucket.key,
        label: bucket.label,
        overlapCount: bucket.overlapCount,
        surveyCompleted: bucket.surveyCompleted,
        pulseCompleted: bucket.pulseCompleted,
        overlapRateSurvey:
          bucket.surveyCompleted > 0
            ? bucket.overlapCount / bucket.surveyCompleted
            : 0,
        overlapRatePulse:
          bucket.pulseCompleted > 0
            ? bucket.overlapCount / bucket.pulseCompleted
            : 0,
      }))
      .sort(
        (a, b) =>
          b.overlapCount - a.overlapCount || a.label.localeCompare(b.label)
      );
  }, [
    pulseRows,
    surveyRows,
    pulseSurveyData,
    debriefData,
    categoryIndex,
    selectedParentOption,
  ]);

  const mergedCategoryRows = useMemo(
    () => mergeCategoryRows(pulseRows, surveyRows, overlapRows),
    [pulseRows, surveyRows, overlapRows]
  );

  const pulseTotals = useMemo(() => {
    const total = mergedPulseRecipients.size;
    const completed = pulseCompleted.size;
    const pending = Math.max(total - completed, 0);
    if (total > 0) {
      return {
        total,
        completed,
        pending,
        rate: completed / total,
      };
    }

    const fallbackTotal =
      pulseSurveyData?.participants?.length ??
      pulseSurveyData?.responses?.length ??
      0;
    const fallbackCompleted = pulseSurveyData?.responses?.length ?? 0;
    const safeCompleted = Math.min(fallbackCompleted, fallbackTotal);

    return {
      total: fallbackTotal,
      completed: safeCompleted,
      pending: Math.max(fallbackTotal - safeCompleted, 0),
      rate: fallbackTotal > 0 ? safeCompleted / fallbackTotal : 0,
    };
  }, [mergedPulseRecipients, pulseCompleted, pulseSurveyData]);

  const surveyTotals = useMemo(() => {
    const requestedCount = surveyRequested.size;
    const derivedTotal =
      requestedCount > 0 ? requestedCount : surveyUniverse.size;
    const derivedCompleted = surveyCompleted.size;

    const scheduleRequestListCount = Array.isArray(schedule?.request_list)
      ? schedule.request_list.length
      : null;
    const selectedRequestListCount = Array.isArray(selectedDebrief?.request_list)
      ? selectedDebrief.request_list.length
      : null;
    const scheduleDataRequestListCount = Array.isArray(
      debriefData?.schedule?.request_list
    )
      ? debriefData.schedule.request_list.length
      : null;

    const explicitTotalCandidate = coalesceFiniteNumber(
      requestedCount > 0 ? requestedCount : null,
      scheduleRequestListCount,
      scheduleDataRequestListCount,
      schedule?.total_requests,
      selectedRequestListCount,
      selectedDebrief?.total_requests,
      selectedDebrief?.total_participants
    );

    const fallbackCompletedCandidate = coalesceFiniteNumber(
      schedule?.total_responses,
      selectedDebrief?.total_responses,
      selectedDebrief?.responses?.length,
      debriefData?.totalResponseCount,
      surveyCompleted.size
    );

    const total =
      explicitTotalCandidate != null ? explicitTotalCandidate : derivedTotal;

    const completedRaw =
      fallbackCompletedCandidate != null
        ? Math.max(fallbackCompletedCandidate, derivedCompleted)
        : derivedCompleted;

    const completed =
      total > 0 ? Math.min(Math.max(completedRaw, 0), total) : completedRaw;
    const pending = Math.max(total - completed, 0);
    const rate = total > 0 ? completed / total : 0;

    return {
      total,
      completed,
      pending,
      rate,
    };
  }, [
    surveyRequested,
    surveyUniverse,
    surveyCompleted,
    schedule,
    selectedDebrief,
    debriefData,
  ]);

  const hasSurveyData =
    surveyTotals.completed > 0 ||
    surveyTotals.total > 0 ||
    (debriefData?.allResponses || []).length > 0;

  if (!pulseSurveyData || !debriefData || !selectedDebrief || !coreData) {
    return (
      <Placeholder>
        Select a pulse and culture audit to view participation metrics.
      </Placeholder>
    );
  }

  if (!hasSurveyData) {
    return (
      <Placeholder>
        Participation data will appear once the selected survey has responses.
      </Placeholder>
    );
  }

  const totalOverlap = overlapRows.reduce(
    (acc, row) => acc + row.overlapCount,
    0
  );

  return (
    <Wrapper>
      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>
            {pulseSurveyData?.surveyInfo?.title || "Pulse Survey"}
          </SummaryLabel>
          <SummaryMetric>{formatPercent(pulseTotals.rate)}</SummaryMetric>
          <SummaryDetail>
            {formatNumber(pulseTotals.completed)} of{" "}
            {formatNumber(pulseTotals.total)} completed
          </SummaryDetail>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>{selectedDebrief?.name || "Survey"}</SummaryLabel>
          <SummaryMetric>{formatPercent(surveyTotals.rate)}</SummaryMetric>
          <SummaryDetail>
            {formatNumber(surveyTotals.completed)} of{" "}
            {formatNumber(surveyTotals.total)} completed
          </SummaryDetail>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Overlap</SummaryLabel>
          <SummaryMetric>
            {formatPercent(
              surveyTotals.completed > 0
                ? totalOverlap / surveyTotals.completed
                : 0
            )}
          </SummaryMetric>
          <SummaryDetail>
            {formatNumber(totalOverlap)} contacts completed both
          </SummaryDetail>
          <SummaryMeta>
            {formatPercent(
              pulseTotals.completed > 0
                ? totalOverlap / pulseTotals.completed
                : 0
            )}{" "}
            of pulse respondents
          </SummaryMeta>
        </SummaryCard>
      </SummaryGrid>

      <Section>
        <SectionHeader>
          <SectionTitle>Participation by Category</SectionTitle>
          {parentOptions.length > 1 && (
            <ControlsRow>
              <ControlLabel>Group by</ControlLabel>
              <ControlSelect
                value={selectedParentKey}
                onChange={(event) => setSelectedParentKey(event.target.value)}
              >
                {parentOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </ControlSelect>
            </ControlsRow>
          )}
        </SectionHeader>
        <DataTable>
          <thead>
            <tr>
              <th>Category</th>
              <th>Pulse</th>
              <th>Survey</th>
              <th>Overlap %</th>
            </tr>
          </thead>
          <tbody>
            {mergedCategoryRows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>
                  <div>
                    {formatNumber(row.pulse.completed)} /{" "}
                    {formatNumber(row.pulse.total)}
                  </div>
                  <TableSubtle>{formatPercent(row.pulse.rate)}</TableSubtle>
                </td>
                <td>
                  <div>
                    {formatNumber(row.survey.completed)} /{" "}
                    {formatNumber(row.survey.total)}
                  </div>
                  <TableSubtle>{formatPercent(row.survey.rate)}</TableSubtle>
                </td>
                <td>
                  <div>{formatPercent(row.overlap.overlapRateSurvey)}</div>
                </td>
              </tr>
            ))}
            {mergedCategoryRows.length === 0 && (
              <tr>
                <td colSpan={4}>No category assignments available.</td>
              </tr>
            )}
          </tbody>
        </DataTable>
      </Section>
    </Wrapper>
  );
};

const mergeCategoryRows = (pulseRows, surveyRows, overlapRows) => {
  const lookup = new Map();

  const ensureEntry = (row) => {
    if (!row) return null;
    if (!lookup.has(row.key)) {
      lookup.set(row.key, {
        key: row.key,
        label: row.label,
        pulse: {
          label: row.label,
          total: 0,
          completed: 0,
          pending: 0,
          rate: 0,
        },
        survey: {
          label: row.label,
          total: 0,
          completed: 0,
          pending: 0,
          rate: 0,
        },
        overlap: {
          label: row.label,
          overlapCount: 0,
          pulseCompleted: 0,
          surveyCompleted: 0,
          overlapRatePulse: 0,
          overlapRateSurvey: 0,
        },
      });
    }
    const entry = lookup.get(row.key);
    if (!entry.label && row.label) {
      entry.label = row.label;
    }
    return entry;
  };

  pulseRows.forEach((row) => {
    const entry = ensureEntry(row);
    if (!entry) return;
    entry.label = row.label;
    entry.pulse = row;
  });

  surveyRows.forEach((row) => {
    const entry = ensureEntry(row);
    if (!entry) return;
    entry.label = row.label;
    entry.survey = row;
  });

  overlapRows.forEach((row) => {
    const entry = ensureEntry(row);
    if (!entry) return;
    entry.label = row.label;
    entry.overlap = row;
  });

  return Array.from(lookup.values()).sort(
    (a, b) =>
      b.survey.total - a.survey.total ||
      b.pulse.total - a.pulse.total ||
      a.label.localeCompare(b.label)
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const SummaryCard = styled.div`
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #ffffff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SummaryLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #4d5562;
`;

const SummaryMetric = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1f2933;
`;

const SummaryDetail = styled.div`
  font-size: 13px;
  color: #384152;
`;

const SummaryMeta = styled.div`
  font-size: 12px;
  color: #6b7483;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2933;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlLabel = styled.span`
  font-size: 12px;
  color: #6b7483;
`;

const ControlSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #d8dde7;
  border-radius: 6px;
  font-size: 12px;
  color: #2f3747;
  background: #ffffff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2d70e2;
    box-shadow: 0 0 0 2px rgba(45, 112, 226, 0.12);
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  overflow: hidden;

  thead {
    background: #f8f9fb;
    text-align: left;
  }

  th,
  td {
    padding: 12px 16px;
    font-size: 13px;
    color: #2f3747;
    border-top: 1px solid #eff2f7;
  }

  th {
    font-weight: 600;
    color: #4d5562;
  }

  tbody tr:first-child td {
    border-top: none;
  }
`;

const TableSubtle = styled.div`
  font-size: 12px;
  color: #6b7483;
`;

const Placeholder = styled.div`
  border: 1px dashed #d8dde7;
  border-radius: 10px;
  padding: 48px 32px;
  text-align: center;
  color: #6b7483;
  background: #fafdff;
`;

export default ComparisonParticipationTab;

